import { useEffect, useState } from "react";
import { looksPortable } from "@strat/format";

import { ImportPanel } from "./ImportPanel";

/** What the desktop app's preload adds: files the OS opened with the app. */
interface DesktopBridge {
  onOpenFile?: (listener: (file: { name: string; bytes: Uint8Array }) => void) => () => void;
}

/**
 * Import from anywhere: drop a `.strategy` / `.backtest` / `.backtest.gz` on
 * any page — or, in the desktop app, double-click one in the file manager —
 * and the import preview opens over whatever you were doing.
 *
 * Drops another part of the page already handles (the import panel's own
 * drop zone) are left alone: those call preventDefault first.
 */
export function GlobalImport({ enabled = true }: { enabled?: boolean }) {
  const [file, setFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    let depth = 0;
    const hasFiles = (e: DragEvent) => !!e.dataTransfer && Array.from(e.dataTransfer.types).includes("Files");
    const enter = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      depth += 1;
      setDragging(true);
    };
    const leave = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      depth = Math.max(0, depth - 1);
      if (depth === 0) setDragging(false);
    };
    const over = (e: DragEvent) => {
      // Without this the browser opens the dropped file as a page.
      if (hasFiles(e)) e.preventDefault();
    };
    const drop = (e: DragEvent) => {
      depth = 0;
      setDragging(false);
      if (e.defaultPrevented || !hasFiles(e)) return;
      e.preventDefault();
      const dropped = e.dataTransfer?.files[0];
      if (dropped && (looksPortable(dropped.name) || dropped.name.toLowerCase().endsWith(".gz")))
        setFile(dropped);
    };
    window.addEventListener("dragenter", enter);
    window.addEventListener("dragleave", leave);
    window.addEventListener("dragover", over);
    window.addEventListener("drop", drop);

    const bridge = (window as unknown as { strat?: DesktopBridge }).strat;
    const stop = bridge?.onOpenFile?.(({ name, bytes }) => setFile(new File([bytes as BlobPart], name)));

    return () => {
      window.removeEventListener("dragenter", enter);
      window.removeEventListener("dragleave", leave);
      window.removeEventListener("dragover", over);
      window.removeEventListener("drop", drop);
      stop?.();
    };
  }, [enabled]);

  useEffect(() => {
    if (!file) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setFile(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [file]);

  return (
    <>
      {dragging && !file && (
        <div className="global-drop" aria-hidden="true">
          <span className="global-drop__label">Drop a .strategy or .backtest file to import it</span>
        </div>
      )}
      {file && (
        <div
          className="import-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Import a file"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setFile(null);
          }}
        >
          <ImportPanel initialFile={file} onClose={() => setFile(null)} />
        </div>
      )}
    </>
  );
}
